import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Activity,
  BookOpen,
  CandlestickChart,
  CircleHelp,
  Coins,
  History,
  Home,
  LogOut,
  Menu,
  MessagesSquare,
  Settings,
  Sun,
  Moon,
  FileText,
  Bell,
  User,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useBranding } from "@/context/BrandingContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { chatService } from "@/services/chatService";
import { formatCurrency } from "@/lib/format";

export const TERMINAL_MENU = [
  { to: "/dashboard", key: "nav.home", label: "Lar", icon: Home, testId: "menu-home" },
  { to: "/terminal", key: "nav.trade", label: "Trade", icon: Activity, testId: "menu-terminal" },
  { to: "/funds", key: "nav.funds", label: "Fundos", icon: Coins, testId: "menu-funds" },
  { to: "/help", key: "nav.tools", label: "Ferramentas de análise", icon: CircleHelp, testId: "menu-help" },
  { to: "/settings", key: "nav.settings", label: "Configurações pessoais", icon: Settings, testId: "menu-settings" },
  { to: "/documents", key: "nav.documents", label: "Documentos", icon: FileText, testId: "menu-documents" },
  { to: "/support", key: "nav.support", label: "Suporte", icon: MessagesSquare, testId: "menu-support" },
];

const SECONDARY = [
  { to: "/guide", key: "nav.info", label: "Informação", icon: BookOpen, testId: "menu-guide" },
];

const MOBILE_TABS = [
  { to: "/terminal?view=chart", key: "tab.chart", icon: CandlestickChart, testId: "mobile-tab-chart", match: "chart" },
  { to: "/terminal?view=assets", key: "tab.assets", icon: Coins, testId: "mobile-tab-assets", match: "assets" },
  { to: "/terminal?view=history", key: "tab.history", icon: History, testId: "mobile-tab-history", match: "history" },
  { to: "/account", key: "tab.account", icon: User, testId: "mobile-tab-account", match: "account" },
];

const marginAlert = (account, t) => {
  const level = account?.margin_level;
  if (level === null || level === undefined) return null;
  if (level <= 50) return { key: "stopout", tone: "text-[#F87171]", text: `${t("notifications.stopout")} ${level}%` };
  if (level <= 100) return { key: "call", tone: "text-[#F87171]", text: `${t("notifications.call")} ${level}%` };
  if (level <= 200) return { key: "warn", tone: "text-[#FBBF24]", text: `${t("notifications.warn")} ${level}%` };
  return null;
};

export const TerminalShell = ({ children, account, metrics = [], mobileSymbol }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useLanguage();
  const { brand } = useBranding();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const mobileView = pathname === "/account" ? "account" : params.get("view") || "chart";
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [support, setSupport] = useState({ messages: 0, items: [] });
  const lastAlertKey = useRef(null);

  useEffect(() => {
    if (user?.role !== "user") return undefined;
    const load = () => chatService.unread().then(setSupport).catch(() => {});
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [user?.role, pathname]);

  useEffect(() => {
    const alert = marginAlert(account, t);
    if (!alert) {
      lastAlertKey.current = null;
      return;
    }
    if (lastAlertKey.current === alert.key) return;
    lastAlertKey.current = alert.key;
    setAlerts((prev) => [{ ...alert, id: `${alert.key}-${Date.now()}`, at: new Date() }, ...prev].slice(0, 5));
  }, [account]);

  const toggleBell = () => {
    if (bellOpen) {
      setBellOpen(false);
      setAlerts([]);
      lastAlertKey.current = null;
    } else {
      setBellOpen(true);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-[#05070C] text-white">
      <aside className="sticky top-0 hidden h-screen w-[68px] flex-col items-center gap-2 border-r border-[#141A26] bg-[#0A0E16] py-5 md:flex">
        <button
          onClick={() => setOpen(true)}
          data-testid="open-menu-button"
          className="mb-6 rounded-md p-2 text-white transition-colors hover:bg-[#141A26]"
        >
          <Menu className="h-5 w-5" />
        </button>
        {TERMINAL_MENU.map((m) => {
          const active = pathname === m.to;
          return (
            <Link
              key={m.to}
              to={m.to}
              data-testid={`rail-${m.testId}`}
              title={t(m.key)}
              className={`rounded-md p-3 transition-colors ${
                active ? "bg-[#132038] text-[#5B9CFF]" : "text-[#8A97AD] hover:bg-[#141A26] hover:text-white"
              }`}
            >
              <m.icon className="h-5 w-5" />
            </Link>
          );
        })}
        <div className="mt-auto flex flex-col items-center gap-2">
          <button onClick={toggle} data-testid="theme-toggle" title="Modo claro/escuro"
                  className="rounded-md p-3 text-[#8A97AD] transition-colors hover:bg-[#141A26] hover:text-white">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          {SECONDARY.map((m) => (
            <Link key={m.to} to={m.to} title={t(m.key)} data-testid={`rail-${m.testId}`}
                  className="rounded-md p-3 text-[#8A97AD] transition-colors hover:bg-[#141A26] hover:text-white">
              <m.icon className="h-5 w-5" />
            </Link>
          ))}
          <button onClick={doLogout} data-testid="rail-logout" title={t("nav.logout")}
                  className="rounded-md p-3 text-[#8A97AD] transition-colors hover:bg-[#141A26] hover:text-[#F87171]">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </aside>

      {open ? (
        <div className="fixed inset-0 z-50 flex" data-testid="menu-drawer">
          <div className="h-full w-[300px] overflow-y-auto border-r border-[#141A26] bg-[#0A0E16] p-6">
            <button onClick={() => setOpen(false)} data-testid="close-menu-button"
                    className="mb-8 rounded-md p-2 text-white transition-colors hover:bg-[#141A26]">
              <X className="h-5 w-5" />
            </button>
            <nav className="space-y-1">
              {TERMINAL_MENU.map((m) => (
                <Link key={m.to} to={m.to} onClick={() => setOpen(false)} data-testid={`drawer-${m.testId}`}
                      className="flex items-center gap-4 rounded-md px-3 py-3 text-sm text-[#C7D0DE] transition-colors hover:bg-[#141A26] hover:text-white">
                  <m.icon className="h-5 w-5 text-[#8A97AD]" /> {t(m.key)}
                </Link>
              ))}
            </nav>
            <div className="my-6 border-t border-[#141A26]" />
            <nav className="space-y-1">
              {SECONDARY.map((m) => (
                <Link key={m.to} to={m.to} onClick={() => setOpen(false)} data-testid={`drawer-${m.testId}`}
                      className="flex items-center gap-4 rounded-md px-3 py-3 text-sm text-[#C7D0DE] transition-colors hover:bg-[#141A26] hover:text-white">
                  <m.icon className="h-5 w-5 text-[#8A97AD]" /> {t(m.key)}
                </Link>
              ))}
              <button onClick={doLogout} data-testid="drawer-logout"
                      className="flex w-full items-center gap-4 rounded-md px-3 py-3 text-sm text-[#C7D0DE] transition-colors hover:bg-[#141A26] hover:text-[#F87171]">
                <LogOut className="h-5 w-5 text-[#8A97AD]" /> {t("nav.logout")}
              </button>
            </nav>
            <div className="mt-6 border-t border-[#141A26] pt-6">
              <LanguageSelector testId="drawer-language-selector" />
            </div>
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-4 border-b border-[#141A26] bg-[#0A0E16]/90 px-4 py-3 backdrop-blur-xl sm:px-6">
          <button onClick={() => setOpen(true)} data-testid="open-menu-mobile"
                  className="rounded-md p-2 text-white transition-colors hover:bg-[#141A26] md:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/terminal" className="flex items-center gap-2" data-testid="terminal-logo">
            <span className="flex h-8 w-8 items-center justify-center rounded-md border border-[#2563EB]/50 bg-[#2563EB]/10">
              <Activity className="h-4 w-4 text-[#5B9CFF]" />
            </span>
            <span className="font-heading text-xs font-semibold uppercase tracking-[0.3em] text-white" data-testid="terminal-brand">{brand}</span>
          </Link>

          <div className="flex flex-1 flex-wrap items-center gap-3">
            {account?.account_id ? (
              <div className="flex items-center gap-3 rounded-md border border-[#1D2637] bg-[#0E1523] px-3 py-1.5">
                <div>
                  <p className="flex items-center gap-2 text-[10px] text-[#8A97AD]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#5B9CFF]" /> {t("metrics.accountId")}
                  </p>
                  <p className="font-mono text-xs text-white" data-testid="account-id">{account.account_id}</p>
                </div>
              </div>
            ) : null}

            {metrics.length ? (
              <div data-testid="metrics-bar" className="hidden flex-wrap items-center gap-x-6 gap-y-2 md:flex">
                {metrics.map((m) => (
                  <div key={m.label} className="leading-tight">
                    <p className="text-[10px] text-[#8A97AD]">{m.label}</p>
                    <p data-testid={m.testId} className={`font-mono text-xs ${m.tone || "text-white"}`}>
                      {m.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="ml-auto flex items-center gap-3 font-mono text-xs md:hidden" data-testid="mobile-topbar-info">
              {mobileSymbol ? <span className="text-white">{mobileSymbol}</span> : null}
              {account?.balance !== undefined && account?.balance !== null ? (
                <span className="text-[#8A97AD]" data-testid="mobile-topbar-balance">
                  {formatCurrency(account.balance)}
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={toggleBell}
                className="relative rounded-md border border-[#1D2637] p-2 text-[#8A97AD] transition-colors hover:text-white"
                data-testid="notifications-button"
              >
                <Bell className="h-4 w-4" />
                {alerts.length + support.messages ? (
                  <span data-testid="notifications-badge"
                        className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4444] px-1 font-mono text-[9px] text-white">
                    {alerts.length + support.messages}
                  </span>
                ) : null}
              </button>
              {bellOpen ? (
                <div data-testid="notifications-panel"
                     className="absolute right-0 z-40 mt-2 w-80 rounded-md border border-[#1D2637] bg-[#0E1523] p-3 shadow-2xl">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">{t("notifications.title")}</p>
                  {support.items.map((s) => (
                    <button
                      key={s.conversation_id}
                      data-testid={`notification-support-${s.conversation_id}`}
                      onClick={() => { setBellOpen(false); navigate("/support"); }}
                      className="w-full border-t border-[#141A26] px-1 py-3 text-left first:border-t-0"
                    >
                      <p className="text-xs text-[#60A5FA]">
                        {s.count} nova(s) mensagem(ns) de {s.from}
                      </p>
                      <p className="mt-1 truncate text-xs text-[#C7D2E5]">{s.preview}</p>
                      <p className="mt-1 font-mono text-[10px] text-[#5C6B82]">{s.subject}</p>
                    </button>
                  ))}
                  {alerts.length === 0 && support.items.length === 0 ? (
                    <p data-testid="notifications-empty" className="px-1 py-4 text-xs text-[#5C6B82]">{t("notifications.empty")}</p>
                  ) : (
                    alerts.map((a) => (
                      <div key={a.id} data-testid={`notification-${a.key}`}
                           className="border-t border-[#141A26] px-1 py-3 first:border-t-0">
                        <p className={`text-xs ${a.tone}`}>{a.text}</p>
                        <p className="mt-1 font-mono text-[10px] text-[#5C6B82]">
                          {a.at.toLocaleTimeString("pt-PT")}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-[#8A97AD] sm:block"
                  data-testid="terminal-user-name">
              {user?.name}
            </span>
            <div className="hidden md:block"><LanguageSelector /></div>
          </div>
        </header>

        <div className="relative min-w-0 flex-1 pb-20 md:pb-0">{children}</div>

        <nav data-testid="mobile-bottom-nav"
             className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-4 border-t border-[#141A26] bg-[#0A0E16]/95 backdrop-blur-xl md:hidden">
          {MOBILE_TABS.map((tab) => {
            const active = mobileView === tab.match;
            return (
              <Link key={tab.to} to={tab.to} data-testid={tab.testId}
                    className={`flex flex-col items-center gap-1 py-3 text-[10px] transition-colors ${
                      active ? "text-[#5B9CFF]" : "text-[#8A97AD]"
                    }`}>
                <tab.icon className="h-5 w-5" />
                {t(tab.key)}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
